import { useState, useEffect } from "react";
import { CheckCircle, Loader2, ArrowRight, AlertCircle, Truck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["courier", "credentials", "connect"];

const OnboardingWizard = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [courier, setCourier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);

  const [config, setConfig] = useState({
    api_url: "https://ffweb30201.pperfect.com/ecomService/v28/Json/",
    account_number: "",
    email: "",
    password: "",
    api_key: "",
  });

  const token = localStorage.getItem("ff_access_token");

  // Fetch defaults on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/settings/courier-config`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const d = res.data;
      if (d.account_number || d.email || d.api_key) {
        setConfig(prev => ({
          ...prev,
          api_url: d.api_url || prev.api_url,
          account_number: d.account_number || prev.account_number,
          email: d.email || prev.email,
          password: d.password || prev.password,
          api_key: d.api_key || prev.api_key,
        }));
      }
    }).catch(() => {});
  }, [token]);

  // Step 1: Select courier
  const selectCourier = (name) => {
    setCourier(name);
    setStep(1);
  };

  // Step 2: Test connection
  const testConnection = async () => {
    setLoading(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/courier-config/test`, config, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestResult(res.data);
      if (res.data.success && res.data.api_key && !config.api_key) {
        setConfig(prev => ({ ...prev, api_key: res.data.api_key }));
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message || "Connection failed";
      console.error("Courier test failed:", err.response?.status, detail);
      setTestResult({ success: false, message: detail });
    }
    setLoading(false);
  };

  // Step 3: Save & sync
  const saveAndSync = async () => {
    setSyncing(true);
    setSyncProgress(10);

    try {
      // Save config
      setSyncProgress(30);
      await axios.post(`${API}/settings/courier-config`, {
        ...config,
        courier: "first_freight",
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSyncProgress(60);

      // Simulate sync progress
      await new Promise(r => setTimeout(r, 800));
      setSyncProgress(80);
      await new Promise(r => setTimeout(r, 600));
      setSyncProgress(100);

      await new Promise(r => setTimeout(r, 500));
      onComplete();
    } catch {
      setTestResult({ success: false, message: "Failed to save configuration" });
      setSyncing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-6 py-5 text-white">
          <h2 className="text-lg font-bold">Set Up Your Courier</h2>
          <p className="text-green-100 text-sm mt-1">Connect your courier account to start creating waybills</p>
          {/* Progress dots */}
          <div className="flex gap-2 mt-3">
            {STEPS.map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-white" : "bg-white/30"}`} />
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Courier Selection */}
          {step === 0 && (
            <div>
              <p className="text-slate-600 text-sm mb-4">Select your courier service provider:</p>
              <button
                onClick={() => selectCourier("first_freight")}
                className="w-full border-2 border-slate-200 hover:border-green-500 rounded-xl p-4 flex items-center gap-4 transition-colors group"
                data-testid="select-first-freight"
              >
                <img src="/first-freight-logo.jpg" alt="First Freight" className="w-14 h-14 rounded-lg object-contain bg-white border border-slate-100 p-1" />
                <div className="text-left flex-1">
                  <p className="font-semibold text-slate-900 group-hover:text-green-700">First Freight</p>
                  <p className="text-xs text-slate-500">Parcel Perfect / First Freight Couriers</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-green-500" />
              </button>

              <div className="mt-4 opacity-50 pointer-events-none">
                <button className="w-full border-2 border-slate-100 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Truck className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-semibold text-slate-400">More couriers coming soon</p>
                    <p className="text-xs text-slate-400">DHL, Aramex, The Courier Guy, etc.</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: API Credentials */}
          {step === 1 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/first-freight-logo.jpg" alt="First Freight" className="w-8 h-8 rounded object-contain" />
                <p className="text-slate-700 font-medium text-sm">First Freight API Configuration</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-slate-700 text-xs font-medium mb-1 block">API URL</label>
                  <input
                    type="text"
                    value={config.api_url}
                    onChange={e => setConfig(prev => ({ ...prev, api_url: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="https://ffweb30201.pperfect.com/ecomService/v28/Json/"
                    data-testid="config-api-url"
                  />
                </div>
                <div>
                  <label className="text-slate-700 text-xs font-medium mb-1 block">Account Number</label>
                  <input
                    type="text"
                    value={config.account_number}
                    onChange={e => setConfig(prev => ({ ...prev, account_number: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="e.g. your account number"
                    data-testid="config-account"
                  />
                </div>
                <div>
                  <label className="text-slate-700 text-xs font-medium mb-1 block">Login Email</label>
                  <p className="text-slate-400 text-xs mb-1">Email used to log into courier portal</p>
                  <input
                    type="email"
                    value={config.email}
                    onChange={e => setConfig(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="your-email@company.com"
                    data-testid="config-email"
                  />
                </div>
                <div>
                  <label className="text-slate-700 text-xs font-medium mb-1 block">Password</label>
                  <p className="text-slate-400 text-xs mb-1">Usually your account number</p>
                  <input
                    type="password"
                    value={config.password}
                    onChange={e => setConfig(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                    placeholder="Courier portal password"
                    data-testid="config-password"
                  />
                </div>
                <div>
                  <label className="text-slate-700 text-xs font-medium mb-1 block">API Key <span className="text-slate-400">(optional — auto-generated from email/password)</span></label>
                  <input
                    type="text"
                    value={config.api_key}
                    onChange={e => setConfig(prev => ({ ...prev, api_key: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 font-mono"
                    placeholder="Will be generated on test"
                    data-testid="config-api-key"
                  />
                </div>
              </div>

              {testResult && (
                <div className={`mt-3 flex items-center gap-2 text-sm px-3 py-2 rounded-md ${
                  testResult.success ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
                }`}>
                  {testResult.success ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {testResult.message}
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => setStep(0)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  onClick={testConnection}
                  disabled={loading || !config.account_number || !config.email}
                  className="flex-1 bg-slate-800 hover:bg-slate-900 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="test-connection-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? "Testing..." : "Test Connection"}
                </button>
                {testResult?.success && (
                  <button
                    onClick={() => setStep(2)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1"
                  >
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Sync */}
          {step === 2 && (
            <div className="text-center">
              {!syncing ? (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Ready to Connect</h3>
                  <p className="text-slate-500 text-sm mt-2 mb-6">
                    Your courier credentials have been verified. Click below to save and synchronise your account.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(1)}
                      className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={saveAndSync}
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white py-2.5 rounded-lg font-medium text-sm"
                      data-testid="sync-btn"
                    >
                      Synchronise & Connect
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <Loader2 className="w-10 h-10 animate-spin text-green-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900">Synchronising...</h3>
                    <p className="text-slate-500 text-sm mt-1">Setting up your courier account</p>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${syncProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">{syncProgress}% complete</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
